import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BlogPublishInfo from '../BlogPublishInfo.tsx';
import microservicesPng from "../../static/blog_microservices_main_image.png";
import microServiceCallPng from "../../static/inter_microservice_api_call.png";
import goodMicroServiceChainPng from "../../static/good_microservice_chain.png";
import badMicroServiceChainPng from "../../static/bad_microservice_chain.png";
import circuitBreakerPng from "../../static/circuit_breaker.png";
import ImageListItem from '@mui/material/ImageListItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Alert from '@mui/material/Alert';

export default function MicroServicesBlog01() {
    return (
        <Container id="MicroServicesBlog01" sx={{ my: { sm: 0, xs: 5 }}} fixed>
          <Box sx={{  minHeight: '50vh' }}>
              <Typography variant="h1" component="h1" align="center">
                Building Microservices in Node.js
              </Typography>
              <BlogPublishInfo date="16th Jan 2025"/>
              <Divider />
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                <img
                    srcSet={microservicesPng}
                    alt={"microservices"}
                    loading="lazy"
                  />
              </ImageListItem>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Overview
              </Typography>
              <Typography variant="body1" align="left">
                <b>Node.js Microservices</b> help us to build <b>scalable, robust, deployment friendly</b> backend services that cater to various needs of applications.
                <br/>In this article will cover the common practises and things to keep in mind which building microservices.
                This article is purely based on my experience in the software industry and there would be difference of opinions around building microservices, constructive criticism is always welcome to help us improve.
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What is a microservice?
              </Typography>
              <Typography variant="body1" align="left">
                A service dedicated to a specific functionality within your application. for example let us say you are building a online shopping system, 
                we would have one microservices for handling payment data, another for handling order information.
                incase a specific fuctionality is huge then we can have a few microservices dedicated to one functionality itself.
                <br/>for example for order processing we can split the functionlity access services like
                `orders`, `orders-cart`, `orders-invoicing`, `orders-shipping`.
                <br/>
                usually it good idea to split functionality into further microservices as code grows more complicated and difficult to maintain
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why microservices?
              </Typography>
              <Typography variant="body1" align="left">
                what compels us to choose microservices over Monolith(a giant single service that caters to entire application needs) service?
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Allows to have dedicated services for individual features</b> that gives more ownership and adds to delivery speed
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Easy Maintenance and Debugging.</b> no need to dive through entire codebase to figure out what is what.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Reduced downtime during deployments.</b> when deploying a specific service we can encounter unforseen long downtimes, 
                    having microservices keeps other parts of system still up and running during deployment of a specific service.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Speed of feature delivery.</b> multiple teams working on respective features can deliver at varying timelines without any dependency on other teams.
                    for example, let us say another team is migrating their service to another database, it does not block your team from working on your service.
                  </ListItem>
                </List>
                <Alert variant="filled" severity="warning">
                  Please evaluate the individual needs of your product before choosing microservices. 
                  incase your product needs are sufficed by one giant monolith then why not just use one giant service?
                  Keep things simple always whenever possible.
                  If you forsee you application growing 5x-10x in next 2 years then you probably need microservices.
                </Alert>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                How do microservices communicate with each other?
              </Typography>
              <Typography variant="body1" align="left">
                Communication between microservices happens via:
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    Internal api call between microservices. to cater to syncronous operations like fetching user orders.
                    <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                      <img
                          srcSet={microServiceCallPng}
                          alt={"microservices"}
                          loading="lazy"
                        />
                    </ImageListItem>
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Events(one service publishes to a topic and another subscribers to the same topic).to cater to asynchronous operations to be performed, for example onPaymentSuccess event sends invoice.
                  </ListItem>
                  </List>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Microservice api call chaining and when to halt?
              </Typography>
              <Typography variant="body1" align="left">
                What is microservice api call chaining?
                <br/>
                the functionality associated with a api call involves distributing logic across multiple relevant services and the parent service keeps calling the child service forming a chain of api calls.
                <br/>
                <br/>
                <Alert variant="filled" severity="warning">
                Take Caution and Avoid api call chain depth hell and Entanglement
                </Alert>
                <br/>
                keep a eye on depth of call, 
                <br/>
                <b>This is ok: </b>
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                  <img
                      srcSet={goodMicroServiceChainPng}
                      alt={"goodmicroservices"}
                      loading="lazy"
                    />
                </ImageListItem>
                Upto depth 6-7 api calls are usually alright
                <br/>
                <b>This is not ok:</b>
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                  <img
                      srcSet={badMicroServiceChainPng}
                      alt={"badmicroservices"}
                      loading="lazy"
                    />
                </ImageListItem>
                <br/>we need to keep in mind that we are building scalable and maintainable functionality and it should be comprehendable to the developers.
                avoid having huge chains of api calls.Cons include developers struggling to keep track of the call chains and also longer call chains add more overhead on the response time of the api.
                <br/>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Circuitbreakers and TTL
              </Typography>
              <Typography variant="body1" align="left">
                what if one microservice in the api call chain is down?
                we need to have mechanisms in place to break the chain to allow us to recover from the problem on its own or solve by developer intervention.
                this saves us unnecessary api calls and even partly helps cyber Dos attacks on our system when bots keep calling apis indefinitely.
                more mechanisms include rate limiting which is out of scope of this specific blog though.
                <br/>
                We can add circuitbreaker middlewares between two api calls in node.js as follows:
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '10vw'} }}>
                  <img
                      srcSet={circuitBreakerPng}
                      alt={"circuitbreaker"}
                      loading="lazy"
                    />
                </ImageListItem>
                <br/>
                <br/>
                Ensure to have ttl for internal api calls as we want to keep the overall response time to client in check.
                for high performance services anything between 100ms-500ms is usually good enough.
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Logging
              </Typography>
              <Typography variant="body1" align="left">
                To ensure logs are helpful write clear and meaningful messages in the logs along with data.
                Take care not to log huge data sets as they just create noise during debugging.
                Logs should be able to be filtered by service so that we can debug efficiently incase of issues.
                commonly used logging systems are kibana, New Relic, Splunk, Datadog.
                <br/>
                We should ideally store logs for atleast upto 1 month to be able to debug issues that can be reported later via customer support.
                Ensure compliance with KPIS and do not log user private information like credit card details and so on.
              </Typography>
          </Box>
        </Container>
    )
};